import config from "config";
import { Howler } from "howler";

if (typeof window !== "undefined") {
	window.Howler = window.Howler || Howler;
}

(function () {
	"use strict";

	const LOCAL_PREFIX = "ytp__";
	const DEFAULT_REWARD_ID = "papa-cherry-reward-default";

	function safeJsonParse(raw, fallback) {
		try {
			return raw ? JSON.parse(raw) : fallback;
		} catch (e) {
			console.warn("[ytgame] save parse failed", e);
			return fallback;
		}
	}

	function hasOwn(obj, key) {
		return Object.prototype.hasOwnProperty.call(obj || {}, key);
	}

	function sanitizeRewardId(value) {
		return String(value || DEFAULT_REWARD_ID)
			.toLowerCase()
			.replace(/[^a-z0-9_-]+/g, "-")
			.replace(/^-+|-+$/g, "")
			.slice(0, 80) || DEFAULT_REWARD_ID;
	}

	const YTP = {
		inEnv: false,
		_firstFrameSent: false,
		_gameReadySent: false,
		_dataLoaded: false,
		_saveObj: null,
		_saveTimer: null,
		_initPromise: null,
		_heartbeatId: null,
		_lastAudioState: null,
		_adsPlaying: false,
		_unsubscribers: [],
		_loadingProgress: 0,
		_wasHidden: false,
		_iosPromptOpen: false,
		_iosAudioPromptSetup: false,
		_pausedSceneKeys: null,
		_pausedByLifecycle: false,
		_lifecycleResumeArmed: false,
		_wasAppPausedBeforeLifecycle: false,
		_playtimeTargetMs: 90000,
		_playtimeAccumulatedMs: 0,
		_playtimeStartedAt: 0,
		_interstitialReady: false,
		_timedInterstitialInProgress: false,

		init() {
			if (this._initPromise) return this._initPromise;

			this._initPromise = (async () => {
				this.inEnv = typeof window.ytgame !== "undefined" && !!window.ytgame.IN_PLAYABLES_ENV;

				if (!this.inEnv) {
					this._saveObj = null;
					this._dataLoaded = true;
					return;
				}

				try {
					const raw = await window.ytgame.game.loadData();
					this._saveObj = safeJsonParse(raw, {});
				} catch (e) {
					console.warn("[ytgame] loadData failed", e);
					this._saveObj = {};
				}

				this._dataLoaded = true;

				try {
					this.applyAudioEnabled(window.ytgame.system.isAudioEnabled());
				} catch (e) {}

				try {
					const offAudio = window.ytgame.system.onAudioEnabledChange((isEnabled) => {
						this.applyAudioEnabled(isEnabled);
					});
					if (typeof offAudio === "function") this._unsubscribers.push(offAudio);
				} catch (e) {}

				try {
					const offPause = window.ytgame.system.onPause(() => this.pauseGame());
					if (typeof offPause === "function") this._unsubscribers.push(offPause);
				} catch (e) {}

				try {
					const offResume = window.ytgame.system.onResume(() => this.resumeGame());
					if (typeof offResume === "function") this._unsubscribers.push(offResume);
				} catch (e) {}

				// Safety net: some Playables hosts fire onPause but do not deliver a
				// matching onResume when the player returns to the tab. Without this,
				// the gameplay scene we paused stays paused forever -> the game looks
				// frozen (PC_01). Resume on the browser's own visible/focus signals too.
				this.armLifecycleResume();
			})();

			return this._initPromise;
		},

		setLoadingProgress(value) {
			const n = Number(value) || 0;
			this._loadingProgress = Math.max(0, Math.min(100, n));
		},

		async getLanguage() {
			if (this.inEnv && window.ytgame && window.ytgame.system && window.ytgame.system.getLanguage) {
				try {
					return (await window.ytgame.system.getLanguage()) || "en";
				} catch (e) {}
			}
			return navigator.language || "en";
		},

		firstFrameReady() {
			if (!this.inEnv || this._firstFrameSent) return;
			this._firstFrameSent = true;
			try {
				window.ytgame.game.firstFrameReady();
				console.log("[ytgame] firstFrameReady");
			} catch (e) {
				console.warn("[ytgame] firstFrameReady failed", e);
			}
		},

		gameReady() {
			if (!this.inEnv || this._gameReadySent) return;
			this.firstFrameReady();
			this._gameReadySent = true;
			try {
				window.ytgame.game.gameReady();
				console.log("[ytgame] gameReady");
			} catch (e) {
				console.warn("[ytgame] gameReady failed", e);
			}
			this.startAudioHeartbeat();
		},

		isAdsPlaying() {
			return !!this._adsPlaying;
		},

		canShowAds() {
			return !!(this.inEnv && window.ytgame && window.ytgame.ads);
		},

		async showInterstitialAd(placement) {
			if (!this.canShowAds() || window.enableADS === false) return false;
			try { if (config && config.noAds) return false; } catch (e) {}
			const wasAppPaused = !!(config && config.pause);
			this._adsPlaying = true;
			this.muteGameAudio(true);
			this.pausePhaserScenes();
			try {
				if (config && config.app && typeof config.app.pause === "function" && !wasAppPaused) {
					config.app.pause(true);
				}
			} catch (e) {}
			try {
				await window.ytgame.ads.requestInterstitialAd();
				return true;
			} catch (e) {
				this.logWarning("interstitial_ad_failed:" + (placement || "unknown"));
				return false;
			} finally {
				this._adsPlaying = false;
				this.resumePhaserScenes();
				try {
					if (config && config.app && typeof config.app.pause === "function" && !wasAppPaused && !this._pausedByLifecycle) {
						config.app.pause(false);
					}
				} catch (e) {}
				this.restoreAudioFromYouTube();
			}
		},

		async showRewardedAd(rewardId) {
			if (!this.canShowAds() || window.enableADS === false) return false;
			try { if (config && config.noAds) return false; } catch (e) {}
			const id = sanitizeRewardId(rewardId || DEFAULT_REWARD_ID);
			this._adsPlaying = true;
			this.muteGameAudio(true);
			this.pausePhaserScenes();
			try {
				return !!(await window.ytgame.ads.requestRewardedAd(id));
			} catch (e) {
				this.logWarning("rewarded_ad_failed:" + id);
				return false;
			} finally {
				this._adsPlaying = false;
				this.resumePhaserScenes();
				this.restoreAudioFromYouTube();
			}
		},

		// Resume the game on the browser's own visible/focus signals as a fallback
		// for hosts that fire onPause without a matching onResume (PC_01).
		armLifecycleResume() {
			if (this._lifecycleResumeArmed) return;
			this._lifecycleResumeArmed = true;
			const tryResume = () => {
				if (this._adsPlaying) return;            // never fight an ad
				if (!this._pausedByLifecycle) return;    // nothing we paused is waiting
				if (document.visibilityState !== "visible") return;
				this.resumeGame();
			};
			document.addEventListener("visibilitychange", () => {
				if (document.visibilityState === "visible") tryResume();
			});
			window.addEventListener("focus", tryResume);
			window.addEventListener("pageshow", tryResume);
		},

		pauseGame() {
			try { this.flushSave(); } catch (e) {}
			this._pausedByLifecycle = true;
			try { this._wasAppPausedBeforeLifecycle = !!(config && config.pause); } catch (e) { this._wasAppPausedBeforeLifecycle = false; }
			this.pausePlaytime();
			this.muteGameAudio(true);
			this.pausePhaserScenes();
			try {
				if (config && config.app && typeof config.app.pause === "function" && !this._wasAppPausedBeforeLifecycle) {
					config.app.pause(true);
				}
			} catch (e) {}
		},

		resumeGame() {
			// Only undo a lifecycle pause here; if nothing was paused by the
			// pause/resume lifecycle, leave the game's own paused scenes (popups,
			// out-of-lives screens, etc.) untouched and just re-sync audio.
			if (!this._pausedByLifecycle) {
				this.restoreAudioFromYouTube();
				return;
			}
			this._pausedByLifecycle = false;
			this.resumePhaserScenes();
			try {
				if (config && config.app && typeof config.app.pause === "function" && !this._wasAppPausedBeforeLifecycle && !this._adsPlaying) {
					config.app.pause(false);
				}
			} catch (e) {}
			this._wasAppPausedBeforeLifecycle = false;
			this.resumePlaytime();
			this.restoreAudioFromYouTube();
		},

		restoreAudioFromYouTube() {
			try {
				if (this.inEnv && window.ytgame && window.ytgame.system) {
					this.applyAudioEnabled(window.ytgame.system.isAudioEnabled());
					return;
				}
			} catch (e) {}
			this.applyAudioEnabled(true);
		},

		applyAudioEnabled(isEnabled) {
			const enabled = !!isEnabled && !this._adsPlaying;
			this._lastAudioState = enabled;

			try {
				if (typeof window.game_data !== "undefined") {
					window.game_data.sound = enabled;
					window.game_data.music = enabled;
				}
			} catch (e) {}

			try {
				if (config) {
					config.youtubeAudioEnabled = enabled;
					config.soundOn = enabled;
					config.soundAccess = enabled;
				}
			} catch (e) {}

			this.muteGameAudio(!enabled);

			try {
				const music = config && config.AllHowlerAudios && config.AllHowlerAudios.MusicBackground;
				if (music) {
					if (!enabled) {
						music.pause();
					} else if (!config.pause && !document.hidden && config._audioUnlocked && typeof music.playing === "function" && !music.playing()) {
						music.play();
					}
				}
			} catch (e) {}

			try {
				if (typeof window.onYTPlayableAudioChanged === "function") {
					window.onYTPlayableAudioChanged(enabled);
				}
			} catch (e) {}
		},

		muteGameAudio(muted) {
			const isMuted = !!muted;
			const audio = this.getGameAudio();

			// Papa Cherry's real Howler audio manager is attached to
			// window.gameInstance.audio, not window.game.audio.
			try {
				if (audio && audio.global && typeof audio.global.mute === "function") {
					audio.global.mute(isMuted);
				}
			} catch (e) {}

			// Phaser sound fallback for other builds that do not use the custom manager.
			try {
				const game = window.gameInstance
					|| ((window.game && window.game.sound) ? window.game : null)
					|| (window.Phaser && window.Phaser.GAMES && window.Phaser.GAMES[0]);
				if (game && game.sound) {
					game.sound.mute = isMuted;
					if (isMuted && typeof game.sound.pauseAll === "function") game.sound.pauseAll();
					if (!isMuted && typeof game.sound.resumeAll === "function") game.sound.resumeAll();
				}
			} catch (e) {}

			// Global Howler fallback; unmuting does not itself unlock Safari audio,
			// the gesture-based unlock is done in resumeGameAudio().
			try {
				if (window.Howler && typeof window.Howler.mute === "function") {
					window.Howler.mute(isMuted);
				}
			} catch (e) {}
		},

		getPhaserGame() {
			try {
				return window.gameInstance
					|| ((window.game && window.game.scene) ? window.game : null)
					|| (window.Phaser && window.Phaser.GAMES && window.Phaser.GAMES[0])
					|| null;
			} catch (e) {
				return null;
			}
		},

		sceneKey(scene) {
			try {
				return (scene.scene && scene.scene.key)
					|| (scene.sys && scene.sys.settings && scene.sys.settings.key)
					|| null;
			} catch (e) {
				return null;
			}
		},

		pausePhaserScenes() {
			try {
				const game = this.getPhaserGame();
				if (!game || !game.scene || !game.scene.scenes) return;
				const paused = [];
				game.scene.scenes.forEach((scene) => {
					try {
						if (scene.scene && scene.scene.isActive()) {
							scene.scene.pause();
							const key = this.sceneKey(scene);
							if (key) paused.push(key);
						}
					} catch (e) {}
				});
				// Remember exactly which scenes WE paused so resume only touches
				// those — never scenes the game itself paused (popups, lives, etc.).
				this._pausedSceneKeys = paused;
			} catch (e) {}
		},

		resumePhaserScenes() {
			try {
				const game = this.getPhaserGame();
				if (!game || !game.scene || !game.scene.scenes) return;
				const keys = this._pausedSceneKeys || [];
				game.scene.scenes.forEach((scene) => {
					try {
						if (!scene.scene || !scene.scene.isPaused()) return;
						const key = this.sceneKey(scene);
						if (key && keys.indexOf(key) !== -1) scene.scene.resume();
					} catch (e) {}
				});
				this._pausedSceneKeys = [];
			} catch (e) {}
		},

		startAudioHeartbeat() {
			if (this._heartbeatId || !this.inEnv) return;
			this._heartbeatId = setInterval(() => {
				try {
					if (this._adsPlaying) return;
					const ytEnabled = !!window.ytgame.system.isAudioEnabled();
					if (typeof window.game_data !== "undefined") {
						if (window.game_data.sound !== ytEnabled || window.game_data.music !== ytEnabled) {
							this.applyAudioEnabled(ytEnabled);
							return;
						}
					}
					if (ytEnabled) this.muteGameAudio(false);
				} catch (e) {}
			}, 1000);
		},

		ensureLoaded() {
			if (this.inEnv && !this._dataLoaded) {
				throw new Error("YT save not loaded yet");
			}
		},

		getKey(key) {
			if (!this.inEnv) return null;
			this.ensureLoaded();
			return hasOwn(this._saveObj, key) ? this._saveObj[key] : null;
		},

		setKey(key, value) {
			if (!this.inEnv) return;
			this.ensureLoaded();
			this._saveObj = this._saveObj || {};
			this._saveObj[key] = value;
			this.scheduleSave();
		},

		removeKey(key) {
			if (!this.inEnv) return;
			this.ensureLoaded();
			if (this._saveObj) delete this._saveObj[key];
			this.scheduleSave();
		},

		scheduleSave() {
			if (!this.inEnv || !this._dataLoaded) return;
			if (this._saveTimer) clearTimeout(this._saveTimer);
			this._saveTimer = setTimeout(() => this.flushSave(), 300);
		},

		flushSave() {
			if (!this.inEnv || !this._dataLoaded) return;
			if (this._saveTimer) {
				clearTimeout(this._saveTimer);
				this._saveTimer = null;
			}
			try {
				return window.ytgame.game.saveData(JSON.stringify(this._saveObj || {}));
			} catch (e) {
				console.warn("[ytgame] saveData failed", e);
			}
		},

		logWarning(message) {
			try {
				if (this.inEnv && window.ytgame && window.ytgame.health && window.ytgame.health.logWarning) {
					window.ytgame.health.logWarning(String(message || "warning"));
				}
			} catch (e) {}
		},


		// ---- Pop The Birds compatibility helpers ----
		syncAudioFromYouTube() {
			this.restoreAudioFromYouTube();
		},

		startPlaytime() {
			if (this._interstitialReady || this._playtimeStartedAt || this._adsPlaying || this._timedInterstitialInProgress) return;
			try { if (config && config.pause) return; } catch (e) {}
			if (document.hidden) return;
			this._playtimeStartedAt = Date.now();
		},

		pausePlaytime() {
			if (!this._playtimeStartedAt) return;
			this._playtimeAccumulatedMs += Date.now() - this._playtimeStartedAt;
			this._playtimeStartedAt = 0;
			this.updatePlaytimeReady();
		},

		resumePlaytime() {
			if (this._adsPlaying || this._timedInterstitialInProgress || this._interstitialReady || this._playtimeStartedAt) return;
			try { if (config && config.pause) return; } catch (e) {}
			if (document.hidden) return;
			this._playtimeStartedAt = Date.now();
		},

		getPlaytimeMs() {
			let total = this._playtimeAccumulatedMs || 0;
			if (this._playtimeStartedAt) total += Date.now() - this._playtimeStartedAt;
			return total;
		},

		updatePlaytimeReady() {
			const target = (config && config.interstitialDelay) || this._playtimeTargetMs || 90000;
			if (this.getPlaytimeMs() >= target) {
				this._interstitialReady = true;
				try { config.interstitialReady = true; } catch (e) {}
			}
			return !!this._interstitialReady;
		},

		resetPlaytime() {
			this._playtimeAccumulatedMs = 0;
			this._playtimeStartedAt = 0;
			this._interstitialReady = false;
			try { config.interstitialReady = false; } catch (e) {}
			this.resumePlaytime();
		},

		async requestTimedInterstitial() {
			this.updatePlaytimeReady();
			try {
				console.log("[ytgame][ad-debug] requestTimedInterstitial",
					"playtimeMs=" + Math.round(this.getPlaytimeMs()),
					"targetMs=" + ((config && config.interstitialDelay) || this._playtimeTargetMs),
					"interstitialReady=" + this._interstitialReady,
					"inProgress=" + this._timedInterstitialInProgress,
					"canShowAds=" + this.canShowAds(),
					"noAds=" + (config && config.noAds),
					"enableADS=" + window.enableADS);
			} catch (e) {}
			if (!this._interstitialReady || this._timedInterstitialInProgress) {
				console.log("[ytgame][ad-debug] BAILED: not enough active playtime yet (or ad in progress)");
				this.resumePlaytime();
				return false;
			}
			try { if (config && config.noAds) { console.log("[ytgame][ad-debug] BAILED: config.noAds is true"); this.resetPlaytime(); return false; } } catch (e) {}

			this._timedInterstitialInProgress = true;
			this.pausePlaytime();

			try {
				if (!this.canShowAds()) {
					console.log("[ytgame][ad-debug] BAILED: canShowAds() false (inEnv=" + this.inEnv + ", ads=" + !!(window.ytgame && window.ytgame.ads) + ")");
					this.resetPlaytime();
					return false;
				}
				console.log("[ytgame][ad-debug] CALLING window.ytgame.ads.requestInterstitialAd()");
				return await this.showInterstitialAd("timed_level_complete");
			} finally {
				this._timedInterstitialInProgress = false;
				this.resetPlaytime();
				this.restoreAudioFromYouTube();
			}
		},

		// ---- iOS "Enable sounds" resume prompt ----
		// On iOS, when the user leaves the app (the WebView/Safari is backgrounded)
		// and comes back, the Web Audio context stays "suspended"/"interrupted" and
		// can ONLY be resumed from a real DOM user gesture. A native alert() OK press
		// does NOT count as a gesture, so we use a custom in-page modal whose OK
		// button click resumes the audio context. We only show it when the sound
		// button is ON and the context is actually still suspended.
		isIOS() {
			try {
				const ua = navigator.userAgent || "";
				const platform = navigator.platform || "";
				const iOSUA = /iP(hone|od|ad)/.test(ua);
				const iPadOS = platform === "MacIntel" && navigator.maxTouchPoints > 1;
				return iOSUA || iPadOS;
			} catch (e) {
				return false;
			}
		},

		// Resolve the game's Howler-based audio manager (window.gameInstance.audio).
		getGameAudio() {
			try {
				const g = window.gameInstance || window.game || (window.Phaser && window.Phaser.GAMES && window.Phaser.GAMES[0]);
				return g && g.audio ? g.audio : null;
			} catch (e) {
				return null;
			}
		},

		getAudioContexts() {
			const contexts = [];
			const add = (ctx) => {
				if (ctx && contexts.indexOf(ctx) === -1) contexts.push(ctx);
			};
			try {
				const a = this.getGameAudio();
				if (a && a.global) add(a.global.ctx);
			} catch (e) {}
			try {
				if (window.Howler) add(window.Howler.ctx);
			} catch (e) {}
			try {
				const g = this.getPhaserGame();
				if (g && g.sound) add(g.sound.context);
			} catch (e) {}
			return contexts;
		},

		getAudioContext() {
			return this.getAudioContexts()[0] || null;
		},

		shouldAudioBeEnabled() {
			// Sound button ON == soundMute is false on the game's audio manager.
			const a = this.getGameAudio();
			if (a && typeof a.soundMute !== "undefined") return !a.soundMute;
			// Fallbacks when the game isn't ready yet.
			try {
				if (this.inEnv && window.ytgame && window.ytgame.system && window.ytgame.system.isAudioEnabled) {
					return !!window.ytgame.system.isAudioEnabled();
				}
			} catch (e) {}
			return this._lastAudioState !== null ? !!this._lastAudioState : false;
		},

		isAudioContextSuspended() {
			return this.getAudioContexts().some((ctx) => {
				return !!ctx && !!ctx.state && ctx.state !== "running" && ctx.state !== "closed";
			});
		},

		// Canonical iOS unlock: play a 1-sample silent buffer through the context
		// inside a user gesture. This forces an "interrupted"/"suspended" AudioContext
		// back to "running" on iOS, where a bare ctx.resume() is often ignored.
		unlockAudioContext(ctx) {
			try {
				if (!ctx || typeof ctx.createBuffer !== "function") return;
				const buffer = ctx.createBuffer(1, 1, 22050);
				const source = ctx.createBufferSource();
				source.buffer = buffer;
				source.connect(ctx.destination);
				if (typeof source.start === "function") source.start(0);
				else if (typeof source.noteOn === "function") source.noteOn(0);
			} catch (e) {}
		},

		// Real user gesture available here -> actually resume the audio context.
		// On iOS after an interruption, resuming the raw AudioContext is not enough;
		// we resume it, play a silent buffer to force "running", let the game re-sync,
		// then re-check shortly after (iOS can need a second pass).
		resumeGameAudio() {
			const audio = this.getGameAudio();
			const contexts = this.getAudioContexts();

			// IMPORTANT: this function is called directly from the OK press.
			// Do every Safari unlock operation synchronously while the gesture is trusted.
			contexts.forEach((ctx) => {
				try {
					this.unlockAudioContext(ctx);
					if (typeof ctx.resume === "function") {
						const result = ctx.resume();
						if (result && typeof result.catch === "function") result.catch(function () {});
					}
					this.unlockAudioContext(ctx);
				} catch (e) {}
			});

			try {
				if (audio && typeof audio.resumeAudioContext === "function") {
					audio.resumeAudioContext();
				}
			} catch (e) {}

			// A real Howl playback in the same tap also re-arms Howler on Safari.
			try {
				if (audio && typeof audio.playClick === "function") audio.playClick();
			} catch (e) {}

			try { this.restoreAudioFromYouTube(); } catch (e) {}
			try { this.muteGameAudio(false); } catch (e) {}

			// Re-sync after Safari finishes switching the context from interrupted to running.
			const retryRestore = () => {
				this.getAudioContexts().forEach((ctx) => {
					try {
						if (ctx.state !== "running" && typeof ctx.resume === "function") {
							const result = ctx.resume();
							if (result && typeof result.catch === "function") result.catch(function () {});
						}
					} catch (e) {}
				});
				try {
					if (audio && typeof audio.resumeAudioContext === "function") audio.resumeAudioContext();
				} catch (e) {}
				try { this.restoreAudioFromYouTube(); } catch (e) {}
				try {
					if (audio && audio.musicLoop && typeof audio.musicLoop.playing === "function" &&
						!audio.musicLoop.playing() && typeof audio.playMusicLoop === "function") {
						audio.playMusicLoop();
					}
				} catch (e) {}
			};

			setTimeout(retryRestore, 100);
			setTimeout(retryRestore, 400);
		},

		// Custom "system-style" alert with a real OK button (iOS-safe gesture).
		showEnableSoundOverlay(onOk) {
			if (this._iosPromptOpen) return;
			this._iosPromptOpen = true;

			const backdrop = document.createElement("div");
			backdrop.id = "ios-sound-prompt";
			backdrop.style.cssText = "position:fixed;top:0;left:0;right:0;bottom:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.4);-webkit-tap-highlight-color:transparent;";

			const box = document.createElement("div");
			box.style.cssText = "min-width:240px;max-width:80%;background:#f7f7f7;border-radius:14px;overflow:hidden;font-family:-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif;text-align:center;box-shadow:0 10px 40px rgba(0,0,0,0.35);";

			const title = document.createElement("div");
			title.textContent = "Enable sounds";
			title.style.cssText = "padding:18px 16px 16px;font-size:17px;font-weight:600;color:#000;line-height:1.3;";

			const btn = document.createElement("button");
			btn.textContent = "OK";
			btn.type = "button";
			btn.style.cssText = "display:block;width:100%;border:none;border-top:1px solid #d6d6d6;background:transparent;color:#007aff;font-size:17px;font-weight:600;padding:13px 0;margin:0;cursor:pointer;outline:none;";

			box.appendChild(title);
			box.appendChild(btn);
			backdrop.appendChild(box);

			// Swallow every pointer/touch/mouse event on the overlay so none of them
			// leak to Phaser's window-level listeners (which would otherwise leave the
			// game's input with a "stuck pointer" and freeze all clicks).
			const swallow = (e) => { try { e.stopPropagation(); } catch (_) {} };
			["touchstart", "touchmove", "touchend", "pointerdown", "pointermove", "pointerup", "mousedown", "mouseup", "click"]
				.forEach((type) => backdrop.addEventListener(type, swallow));

			let done = false;
			const close = () => {
				try { if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop); } catch (e) {}
				this._iosPromptOpen = false;
			};
			const handler = (ev) => {
				if (done) return;
				done = true;
				if (ev) {
					try { ev.preventDefault(); } catch (e) {}   // suppress the synthesized click
					try { ev.stopPropagation(); } catch (e) {}
				}
				try { if (typeof onOk === "function") onOk(); } catch (e) {}
				// Defer removal: removing the touched node inside touchend makes iOS drop
				// the following touch events. Removing it a tick later avoids that.
				setTimeout(close, 60);
			};
			// Use the earliest trusted gesture. On Safari after app switching,
			// waiting for click/touchend can be too late to unlock Web Audio.
			btn.addEventListener("touchstart", handler, { passive: false });
			btn.addEventListener("pointerdown", handler);
			btn.addEventListener("click", handler);

			(document.body || document.documentElement).appendChild(backdrop);
		},

		setupIOSAudioResumePrompt() {
			if (this._iosAudioPromptSetup || !this.isIOS()) return;
			this._iosAudioPromptSetup = true;

			const onReturn = () => {
				if (!this._wasHidden) return;        // only after a real background -> foreground cycle
				this._wasHidden = false;
				if (document.visibilityState !== "visible") return;
				if (this._adsPlaying) return;        // never interrupt an ad
				if (this._iosPromptOpen) return;

				// Let the game's own onPageVisible resume attempt run first (it works on
				// Android / when no gesture is needed). On iOS it stays suspended, so we
				// then ask the user with a real-button modal.
				setTimeout(() => {
					if (this._adsPlaying || this._iosPromptOpen) return;
					if (document.visibilityState !== "visible") return;
					if (!this.shouldAudioBeEnabled()) return;     // sound button must be ON
					if (!this.isAudioContextSuspended()) return;  // already running -> nothing to do
					this.showEnableSoundOverlay(() => this.resumeGameAudio());
				}, 300);
			};

			document.addEventListener("visibilitychange", () => {
				if (document.visibilityState === "hidden") this._wasHidden = true;
				else if (document.visibilityState === "visible") onReturn();
			});
			window.addEventListener("pageshow", onReturn);
			window.addEventListener("focus", onReturn);
		}
	};

	window.YouTubePlayables = YTP;

	// Compatibility storage used by the existing bundled game code.
	// In YouTube it persists through ytgame.game.loadData/saveData; outside YouTube it uses localStorage.
	window.ytStorage = window.ytStorage || {
		getItem(key) {
			try {
				if (YTP.inEnv) return YTP.getKey(key);
				return window.localStorage ? window.localStorage.getItem(LOCAL_PREFIX + key) : null;
			} catch (e) { return null; }
		},
		setItem(key, value) {
			try {
				if (YTP.inEnv) {
					YTP.setKey(key, String(value));
				} else if (window.localStorage) {
					window.localStorage.setItem(LOCAL_PREFIX + key, String(value));
				}
			} catch (e) {}
		},
		removeItem(key) {
			try {
				if (YTP.inEnv) {
					YTP.removeKey(key);
				} else if (window.localStorage) {
					window.localStorage.removeItem(LOCAL_PREFIX + key);
				}
			} catch (e) {}
		},
		keys() {
			try {
				if (YTP.inEnv) return Object.keys(YTP._saveObj || {});
				if (!window.localStorage) return [];
				const keys = [];
				for (let i = 0; i < window.localStorage.length; i++) {
					const key = window.localStorage.key(i);
					if (key && key.indexOf(LOCAL_PREFIX) === 0) keys.push(key.slice(LOCAL_PREFIX.length));
				}
				return keys;
			} catch (e) { return []; }
		},
		flushNow() {
			try { return YTP.flushSave(); } catch (e) {}
		}
	};

	window._ytDataPromise = YTP.init();

	// iOS-only: prompt to re-enable sounds when the user returns to the app.
	YTP.setupIOSAudioResumePrompt();

	window.addEventListener("pagehide", () => YTP.flushSave());
	window.addEventListener("beforeunload", () => YTP.flushSave());
	document.addEventListener("visibilitychange", () => {
		if (document.hidden) YTP.flushSave();
	});
})();

export default window.YouTubePlayables;
